import React, { useContext, useRef, useState } from "react";
import { Modal, Table, Badge, Spinner } from "react-bootstrap";
import { FaFileUpload } from "react-icons/fa";
import api, { BASE_URL } from "../../Utils/api";
import { GlobalContext } from "../../Utils/GlobalContext";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import { Salvar, Cancelar } from "../Generals/buttons";
import { parsePlanilhaFuncionarios } from "../../Utils/importacaoFuncionarios";

// Importação em lote de funcionários (roadmap #37): lê a planilha gerada pelo
// botão "Modelo XLSX", faz a pré-validação inteiramente no navegador (sem
// enviar nada ao servidor ainda) e só chama o backend depois que o usuário
// confirma o relatório de inconsistências.
const FuncionariosImportarModal = ({ show, handleClose, fetchFuncionarios }) => {
  const { toast } = useFeedback();
  const { globalData } = useContext(GlobalContext);
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");
  const fileInputRef = useRef(null);

  const [analisando, setAnalisando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null); // { validos, invalidos, totalLinhas }
  const [erro, setErro] = useState(null);
  const [resumoImportacao, setResumoImportacao] = useState(null); // { inseridos, erros }

  const resetar = () => {
    setResultado(null);
    setErro(null);
    setResumoImportacao(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFecharModal = () => {
    resetar();
    handleClose();
  };

  const handleArquivoSelecionado = async (e) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;

    setErro(null);
    setResultado(null);
    setResumoImportacao(null);
    setAnalisando(true);
    try {
      const empresasResponse = await api.get(
        `${BASE_URL}/api/empresas/ListarEmpresasParaFuncionarios/${entidadeID}?incluirEntidade=1`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
      );
      const empresas = empresasResponse.data?.data || [];

      const arrayBuffer = await arquivo.arrayBuffer();
      const analise = parsePlanilhaFuncionarios(arrayBuffer, empresas);
      setResultado(analise);
      if (analise.totalLinhas === 0) {
        toast.warning("A planilha não tem nenhuma linha preenchida na aba Funcionarios.");
      }
    } catch (err) {
      console.error("Erro ao analisar planilha de funcionários:", err);
      setErro(err.message || "Erro ao ler a planilha. Verifique se o arquivo é o modelo correto.");
    } finally {
      setAnalisando(false);
    }
  };

  const handleImportar = async () => {
    if (!resultado || resultado.validos.length === 0) return;

    setImportando(true);
    setErro(null);
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        id_entidade: entidadeID,
      };
      const { data } = await api.post(
        `${BASE_URL}/api/funcionarios/ImportarFuncionarios`,
        { funcionarios: resultado.validos },
        { headers }
      );
      setResumoImportacao(data.data);
      toast.success(data.message || "Importação concluída.");
      await fetchFuncionarios?.();
    } catch (err) {
      console.error("Erro ao importar funcionários:", err);
      setErro(
        err.response?.data?.message || "Erro ao importar funcionários. Tente novamente."
      );
    } finally {
      setImportando(false);
    }
  };

  const todasLinhas = resultado ? [...resultado.validos, ...resultado.invalidos].sort((a, b) => a.linha - b.linha) : [];

  return (
    <Modal
      show={show}
      onHide={handleFecharModal}
      backdrop="static"
      size="lg"
      dialogClassName="modal-dialog-top"
    >
      <Modal.Header closeButton style={{ background: "var(--surface-sunken)", borderColor: "var(--border-default)", paddingBottom: 12 }}>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-body)" }}>
          Importar Funcionários
          <span style={{ display: "block", fontSize: "0.82rem", fontWeight: 400, color: "var(--text-muted)", marginTop: 2 }}>
            Envie a planilha preenchida a partir do modelo (aba "Funcionarios").
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "var(--surface-card)", color: "var(--text-body)" }}>
        {erro && (
          <div className="alert alert-danger" role="alert">{erro}</div>
        )}

        {!resumoImportacao && (
          <div className="mb-3">
            <label className="form-label d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
              <FaFileUpload />
              <span>{analisando ? "Analisando..." : "Selecionar planilha (.xlsx)"}</span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="d-none"
                onChange={handleArquivoSelecionado}
                disabled={analisando || importando}
              />
            </label>
          </div>
        )}

        {analisando && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> Lendo planilha...
          </div>
        )}

        {resultado && !resumoImportacao && (
          <>
            <div className="d-flex gap-3 mb-3">
              <Badge bg="success">{resultado.validos.length} válido(s)</Badge>
              <Badge bg="danger">{resultado.invalidos.length} com erro</Badge>
              <Badge bg="secondary">{resultado.totalLinhas} linha(s) preenchida(s)</Badge>
            </div>

            {todasLinhas.length > 0 && (
              <div className="table-responsive" style={{ maxHeight: 320, overflowY: "auto" }}>
                <Table hover size="sm" className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Linha</th>
                      <th>Nome</th>
                      <th>Empresa</th>
                      <th>Situação</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todasLinhas.map((item) => (
                      <tr key={item.linha} className={item.motivos ? "table-danger" : undefined}>
                        <td>{item.linha}</td>
                        <td>{item.nome || "—"}</td>
                        <td>{item.empresaTexto || "—"}</td>
                        <td>{item.situacao === "I" ? "Inativo" : "Ativo"}</td>
                        <td>
                          {item.motivos ? (
                            <span className="text-danger small">{item.motivos.join(" ")}</span>
                          ) : (
                            <span className="text-success small">OK</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}

        {resumoImportacao && (
          <div className="alert alert-success" role="alert">
            <strong>{resumoImportacao.inseridos}</strong> funcionário(s) importado(s) com sucesso.
            {resumoImportacao.erros?.length > 0 && (
              <div className="mt-2">
                <strong>{resumoImportacao.erros.length}</strong> linha(s) não importada(s):
                <ul className="mb-0">
                  {resumoImportacao.erros.map((e) => (
                    <li key={e.linha}>Linha {e.linha}: {e.mensagem}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="modal-footer-padrao">
        <div className="modal-footer-acoes-esquerda" />
        <div className="modal-footer-acoes-direita">
          <Cancelar onClick={handleFecharModal} disabled={importando} />
          {!resumoImportacao && (
            <Salvar
              onClick={handleImportar}
              loading={importando}
              disabled={!resultado || resultado.validos.length === 0}
              title={`Importar ${resultado?.validos.length || 0} funcionário(s)`}
            />
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default FuncionariosImportarModal;
